import {
  CheckCircleFilled,
  CloseCircleOutlined,
  ContainerOutlined,
  EditOutlined,
  MailOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Checkbox,
  Dropdown,
  Image,
  message,
  Modal,
  Table,
  Tag,
} from 'antd';
import { useContext, useState } from 'react';
import EditUserModal from './editUserModal';
import {
  deactivateUser,
  inviteUser,
  reactivateUser,
} from 'services/api/workspace';
import styles from 'styles/components/userTable.module.scss';
import ProfileContext from 'services/profileContext';
import { isAdmin } from 'utils/utils';

const UserTable = ({
  users = [],
  filterSearch = '',
  participantsAlreadyInCycle = [],
  selectedUsers = [],
  setSelectedUsers = () => {},
  showSelection = false,
  showEditable = false,
  fetchUsers = () => {},
}) => {
  const [userEditing, setUserEditing] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [loadingDeactivate, setLoadingDeactivate] = useState(false);
  const [loadingReactivate, setLoadingReactivate] = useState(false);
  const { profile } = useContext(ProfileContext);

  const deactivateUserCall = (userId) => {
    setLoadingDeactivate(true);
    deactivateUser(userId)
      .then(() => {
        message.success('User deactivated successfully');
        fetchUsers();
      })
      .catch((e) => {
        console.error('Error deactivating user', e);
      })
      .finally(() => {
        setLoadingDeactivate(false);
      });
  };
  const reactivateUserCall = (userId) => {
    setLoadingReactivate(true);
    reactivateUser(userId)
      .then(() => {
        message.success('User reactivated successfully');
        fetchUsers();
      })
      .catch((e) => {
        console.error('Error reactivating user', e);
      })
      .finally(() => {
        setLoadingReactivate(false);
      });
  };

  return (
    <>
      <Table
        scroll={{ x: 800 }}
        loading={loadingDeactivate || loadingReactivate}
        dataSource={users.filter(
          (user) =>
            user.profile.firstName
              .toLowerCase()
              .includes(filterSearch.toLowerCase()) ||
            user.profile.lastName
              .toLowerCase()
              .includes(filterSearch.toLowerCase()) ||
            user.email.toLowerCase().includes(filterSearch.toLowerCase())
        )}
        rowKey={(record) => record._id}
        rowClassName={(record) => {
          if (record.deactivatedAt) {
            return styles.deactivatedUser;
          }
          return '';
        }}
        columns={[
          ...(showSelection
            ? [
                {
                  dataIndex: 'selected',
                  key: 'selected',
                  width: 50,
                  fixed: 'left',
                  render: (text, record) => {
                    return (
                      <Checkbox
                        disabled={participantsAlreadyInCycle.includes(
                          record._id
                        )}
                        checked={
                          participantsAlreadyInCycle.includes(record._id) ||
                          selectedUsers.includes(record._id)
                        }
                        onChange={(e) => {
                          setSelectedUsers((selected) =>
                            selected.includes(record._id)
                              ? selected.filter((id) => id !== record._id)
                              : [...selected, record._id]
                          );
                        }}
                      />
                    );
                  },
                },
              ]
            : []),
          {
            fixed: 'left',
            title: 'Name',
            dataIndex: 'profile',
            key: 'name',
            width: 180,
            render: (text, record) => {
              return (
                <div>
                  {record.profile?.profilePictureUrl && (
                    <Avatar
                      src={record.profile.profilePictureUrl}
                      size={34}
                      style={{ marginRight: 6 }}
                    />
                  )}
                  {record.profile.firstName} {record.profile.lastName}
                  {isAdmin(record) && (
                    <Tag
                      style={{
                        marginLeft: 8,
                        padding: '2px 4px ',
                        fontSize: 12,
                      }}
                      color="red"
                    >
                      ADMIN
                    </Tag>
                  )}
                </div>
              );
            },
          },
          {
            title: 'Job Title',
            dataIndex: 'jobTitle',
            key: 'jobTitle',
            width: 150,
            render: (text, record) => {
              return <div>{record.profile.jobTitle}</div>;
            },
          },
          {
            title: 'Work Email',
            dataIndex: 'email',
            key: 'email',
            render: (text, record) => {
              return (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {record.slackUserId ? (
                    <div style={{ marginRight: 6 }}>
                      <Image
                        src={'/img/slack_80.png'}
                        width={16}
                        height={16}
                        preview={false}
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                  <div
                    style={{
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {text}
                  </div>
                </div>
              );
            },
          },
          {
            width: 100,
            title: 'Received invitation?',
            dataIndex: 'invited',
            key: 'invited',
            render: (invited, record) => {
              return record.slackUserId ? (
                record.isSlackUserAlreadyInvited ? (
                  <>
                    <CheckCircleFilled style={{ color: '#00d300' }} />
                  </>
                ) : (
                  <>-</>
                )
              ) : (
                <div>
                  {invited ? (
                    <CheckCircleFilled style={{ color: '#00d300' }} />
                  ) : (
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      <CloseCircleOutlined style={{ color: '#FF0000' }} />
                      {showEditable &&
                        !record.emailVerified && (
                          <Button
                            type="link"
                            size="small"
                            icon={<MailOutlined />}
                            onClick={() => {
                              Modal.confirm({
                                title: 'Invite user',
                                content:
                                  'Are you sure you want to invite this user? This will send an email to the user to join the workspace.',
                                onOk: () => {
                                  setLoadingInvite(true);
                                  inviteUser({
                                    userId: record._id,
                                  })
                                    .then(() => {
                                      message.success(
                                        'User invited successfully'
                                      );
                                    })
                                    .catch((e) => {
                                      console.error('Error inviting user', e);
                                      message.error(
                                        'Error inviting user, please try again'
                                      );
                                    })
                                    .finally(() => {
                                      setLoadingInvite(false);
                                    });
                                },
                                cancelText: 'No, cancel',
                                okText: 'Yes, invite',
                                okButtonProps: {
                                  icon: <MailOutlined />,
                                  loading: loadingInvite,
                                },
                              });
                            }}
                          >
                            Invite
                          </Button>
                        )}
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            width: 100,
            title: 'Account verified?',
            dataIndex: 'emailVerified',
            key: 'emailVerified',
            render: (invited, u) => {
              return u.slackUserId ? (
                u.isSlackUserAlreadyInvited ? (
                  <>
                    <CheckCircleFilled style={{ color: '#00d300' }} />
                  </>
                ) : (
                  <>-</>
                )
              ) : (
                <div>
                  {invited ? (
                    <CheckCircleFilled style={{ color: '#00d300' }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: '#FF0000' }} />
                  )}
                </div>
              );
            },
          },
          ...(showEditable
            ? [
                {
                  width: 60,
                  title: '',
                  dataIndex: 'actions',
                  key: 'actions',
                  render: (text, record) => {
                    return (
                      <div
                        style={{
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <Dropdown
                          menu={{
                            onClick: (e) => {
                              if (e.key === 'deactivate') {
                                Modal.confirm({
                                  title: 'Deactivate user',
                                  content:
                                    'Are you sure you want to deactivate this user?',
                                  onOk: () => {
                                    deactivateUserCall(record._id);
                                  },
                                  okText: 'Yes, Deactivate',
                                  cancelText: 'No, keep it active',
                                });
                              }
                              if (e.key === 'reactivate') {
                                Modal.confirm({
                                  title: 'Reactivate user',
                                  content:
                                    'Are you sure you want to reactivate this user?',
                                  onOk: () => {
                                    reactivateUserCall(record._id);
                                  },
                                  okText: 'Yes, Reactivate',
                                  cancelText: 'No, keep it deactivated',
                                });
                              }
                            },
                            items: [
                              {
                                key: 'edit',
                                label: 'Edit',
                                icon: <EditOutlined />,
                                onClick: () => setUserEditing(record),
                              },
                              ...(record.deactivatedAt
                                ? [
                                    {
                                      key: 'reactivate',
                                      label: 'Reactivate',
                                      icon: <ContainerOutlined />,
                                    },
                                  ]
                                : [
                                    {
                                      key: 'deactivate',
                                      label: 'Deactivate',
                                      icon: <ContainerOutlined />,
                                      disabled: record._id === profile._id,
                                    },
                                  ]),
                            ],
                          }}
                          trigger={['click']}
                        >
                          <Button type="link" size="small">
                            <MoreOutlined />
                          </Button>
                        </Dropdown>
                      </div>
                    );
                  },
                },
              ]
            : []),
        ]}
        pagination={true}
        defaultPageSize={20}
      />
      <EditUserModal
        user={userEditing}
        open={!!userEditing}
        onCancel={(refresh = false) => {
          if (refresh) {
            fetchUsers();
          }
          setUserEditing(null);
        }}
      />
    </>
  );
};

export default UserTable;
